import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api } from "../lib/api";
import { useT } from "../contexts/LanguageContext";
import { PublicHeader } from "./PublicPages";
import Footer from "../components/app/Footer";
import SEOHead from "../components/app/SEOHead";
import { CaretLeft, Calendar, Clock } from "@phosphor-icons/react";

export default function BlogPost() {
  const { slug } = useParams();
  const { t, lang } = useT();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading");
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/blog/${slug}?lang=${lang}`);
        setPost(data);
        setStatus("ok");
      } catch (e) {
        setStatus(e?.response?.status === 404 ? "notfound" : "error");
      }
    })();
  }, [slug, lang]);

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(lang === "tr" ? "tr-TR" : "en-US", {
        year: "numeric", month: "long", day: "numeric",
      });
    } catch { return ""; }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen flex flex-col">
        <PublicHeader />
        <main className="flex-1 max-w-3xl mx-auto px-4 py-16 w-full" data-testid="blog-post-loading">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-3/4"></div>
            <div className="h-4 bg-slate-200 rounded w-1/2"></div>
            <div className="h-4 bg-slate-100 rounded"></div>
            <div className="h-4 bg-slate-100 rounded"></div>
            <div className="h-4 bg-slate-100 rounded w-5/6"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (status === "notfound" || status === "error") {
    return (
      <div className="min-h-screen flex flex-col">
        <PublicHeader />
        <main className="flex-1 max-w-2xl mx-auto px-4 py-16 w-full text-center" data-testid="blog-post-notfound">
          <div className="overline text-slate-500">404</div>
          <h1 className="font-display text-4xl font-bold mt-2">{t("blog.notfound_title")}</h1>
          <p className="text-slate-600 mt-3">{t("blog.notfound_body")}</p>
          <Link to="/blog" className="btn-primary mt-6 inline-flex">
            {t("blog.back_to_list")}
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const title = lang === "tr" ? post.title_tr : (post.title_en || post.title_tr);
  const body = lang === "tr" ? post.body_md_tr : (post.body_md_en || post.body_md_tr);
  const readMins = Math.max(1, Math.round((body || "").split(/\s+/).length / 220));

  // BlogPosting JSON-LD
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: title,
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    author: { "@type": "Organization", name: post.author_name || "PMCoach AI" },
    publisher: {
      "@type": "Organization",
      name: "PMCoach AI",
      logo: { "@type": "ImageObject", url: "https://pmcoach-ai.com/logo-full.png" },
    },
    description: lang === "tr" ? (post.seo_description_tr || post.excerpt_tr) : (post.seo_description_en || post.excerpt_en),
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://pmcoach-ai.com/blog/${post.slug}` },
    keywords: (post.tags || []).join(", "),
  };

  // SEO title override
  const seoTitle = lang === "tr" ? (post.seo_title_tr || post.title_tr) : (post.seo_title_en || post.title_en || post.title_tr);
  const seoDescription = lang === "tr" ? (post.seo_description_tr || post.excerpt_tr) : (post.seo_description_en || post.excerpt_en || post.excerpt_tr);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Override default page SEO strings via inline meta */}
      <SEOHead pageKey="blog_post" jsonLd={jsonLd} />
      <PublicHeader />
      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full" data-testid="blog-post-page">
        <button
          onClick={() => navigate("/blog")}
          className="text-sm text-slate-500 hover:text-[#002FA7] inline-flex items-center gap-1 mb-6"
          data-testid="blog-back-btn"
        >
          <CaretLeft size={14} weight="bold" /> {t("blog.back_to_list")}
        </button>

        {post.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {post.tags.map((tag) => (
              <span key={tag} className="text-xs px-2 py-1 rounded-full bg-[#DBEAFE] text-[#002FA7] font-medium">
                {tag}
              </span>
            ))}
          </div>
        )}

        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight" data-testid="blog-post-title">
          {title}
        </h1>

        <div className="flex items-center gap-4 text-sm text-slate-500 mt-4">
          <span className="inline-flex items-center gap-1"><Calendar size={14} weight="bold" /> {fmtDate(post.published_at)}</span>
          <span className="inline-flex items-center gap-1"><Clock size={14} weight="bold" /> {readMins} {t("blog.min_read")}</span>
          <span>· {post.author_name}</span>
        </div>

        <article className="prose prose-slate max-w-none mt-8 prose-headings:font-display prose-headings:tracking-tight prose-a:text-[#002FA7] prose-a:font-semibold prose-strong:text-slate-900" data-testid="blog-post-body">
          <ReactMarkdown>{body || ""}</ReactMarkdown>
        </article>

        <div className="mt-16 p-6 bg-[#F7F8FA] rounded-2xl border border-slate-200" data-testid="blog-cta">
          <div className="overline text-[#002FA7]">PMCoach AI</div>
          <h2 className="font-display text-2xl font-bold mt-2">{t("blog.cta_title")}</h2>
          <p className="text-sm text-slate-600 mt-2">{t("blog.cta_body")}</p>
          <Link to="/register" className="btn-primary mt-4 inline-flex" data-testid="blog-cta-button">
            {t("blog.cta_button")}
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
